import { config } from '../config';
import type { EmbeddingIndexFile, MatchResult, PokemonRecord } from '../types';

export class PokemonMatcher {
  private readonly quantized: Int8Array;
  private readonly byId: Map<number, PokemonRecord>;

  constructor(
    private readonly index: EmbeddingIndexFile,
    pokemon: PokemonRecord[],
  ) {
    this.quantized = decodeBase64Int8(index.data);
    this.byId = new Map(pokemon.map((entry) => [entry.id, entry]));

    const expected = index.ids.length * index.dimension;
    if (this.quantized.length !== expected) {
      throw new Error(`向量文件长度异常：预期 ${expected}，实际 ${this.quantized.length}。`);
    }
  }

  search(query: Float32Array, imageHash: number): MatchResult[] {
    if (query.length !== this.index.dimension) {
      throw new Error(`模型向量维度不一致：预期 ${this.index.dimension}，实际 ${query.length}。`);
    }

    const results: MatchResult[] = [];
    const jitterRange = config.matching.stableJitter;

    for (let row = 0; row < this.index.ids.length; row += 1) {
      const id = this.index.ids[row];
      if (id === undefined) continue;
      const pokemon = this.byId.get(id);
      if (!pokemon) continue;

      const offset = row * this.index.dimension;
      let score = 0;
      for (let column = 0; column < query.length; column += 1) {
        score += query[column]! * (this.quantized[offset + column]! / 127);
      }

      score += stableJitter(imageHash, id) * jitterRange;
      if (score < config.matching.minimumRawSimilarity) continue;

      results.push({
        pokemon,
        rawSimilarity: score,
        displayScore: toDisplayScore(score),
      });
    }

    results.sort((a, b) => b.rawSimilarity - a.rawSimilarity);
    return results.slice(0, config.matching.topK);
  }
}

export function normalizeVector(input: Float32Array): Float32Array {
  let squared = 0;
  for (const value of input) squared += value * value;
  const norm = Math.sqrt(squared) || 1;
  return Float32Array.from(input, (value) => value / norm);
}

export function hashImageData(data: Uint8ClampedArray): number {
  let hash = 2166136261;
  const step = Math.max(1, Math.floor(data.length / 4096));
  for (let index = 0; index < data.length; index += step) {
    hash ^= data[index]!;
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function stableJitter(imageHash: number, pokemonId: number): number {
  let value = imageHash ^ Math.imul(pokemonId, 0x9e3779b1);
  value ^= value >>> 16;
  value = Math.imul(value, 0x7feb352d);
  value ^= value >>> 15;
  value = Math.imul(value, 0x846ca68b);
  value ^= value >>> 16;
  return ((value >>> 0) / 0xffffffff) * 2 - 1;
}

function toDisplayScore(raw: number): number {
  const min = config.matching.displayScoreRawMin;
  const max = config.matching.displayScoreRawMax;
  const normalized = (raw - min) / Math.max(0.0001, max - min);
  return Math.round(Math.min(1, Math.max(0, normalized)) * 100);
}

function decodeBase64Int8(base64: string): Int8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Int8Array(bytes.buffer);
}
